/**
 * Top bar: breadcrumbs, global search, the location scope, notifications, attention, theme and profile.
 *
 * There is no environment badge here. The one label the desk shows is the strip across the top of a TEST organisation
 * (AppShell, TestOrganisationStrip). The unused badge hook that once read the source registry was removed on the owner's
 * decision of 2026-10-25 (audit ENV-1, option C): the live organisation shows no STAGING label.
 */
import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { HUBS, NAV_SECTIONS, RECORD_HOME, menuLabel, screenById, workspaceOf } from '@rtcom/domain';
import { useRecordTitle } from '../ui/recordTitle';
import { Bell, Menu, Search, TriangleAlert } from 'lucide-react';
import { useSession } from '../lib/session';
import { rpc, supabase } from '../lib/supabase';
import { useNavLang } from './navLang';
import { cx } from '../ui/primitives';
import { LocationContext, UserMenu } from './UserMenu';
import { ActivityMoment } from '../ui/moments';
import { AttentionTray, NotificationsPanel } from './AttentionTray';
import { screenAt } from './Sidebar';
import { serverNow, useBusinessDay } from '../lib/businessDay';

const SECTION_LABEL = new Map(NAV_SECTIONS.map((s) => [s.id, s.label]));

function useCurrentScreen() {
  const { pathname } = useLocation();
  // The registered screen at this address, record pages (`/customers/:id`) included (Sidebar's screenAt).
  return screenAt(pathname);
}

export function TopBar({
  onOpenPalette, theme, onToggleTheme, onOpenNav,
}: {
  onOpenPalette: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  /** On a phone: open the menu as a drawer (D19 phase 5). */
  onOpenNav?: () => void;
}) {
  const screen = useCurrentScreen();
  const [trayOpen, setTrayOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  return (
    <>
      <header className="shell-topbar flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-4">
        {onOpenNav ? (
          <button type="button" onClick={onOpenNav} aria-label="Open the menu"
            className="rounded-[var(--radius-control)] p-1.5 text-ink-muted hover:bg-surface-sunken hover:text-ink">
            <Menu size={17} />
          </button>) : null}
        <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
          <ol className="flex items-center gap-1.5 text-small text-ink-muted">
            <li>
              <Link to="/" className="hover:text-ink">
                RTCOM
              </Link>
            </li>
            {screen ? (
              <>
                <li aria-hidden>/</li>
                {/* v7 Part E: Workspace / List / Record, or Workspace / Hub / Tab, or Workspace / Screen, in Part E's names. */}
                <li>{workspaceOf(screen.id)?.label ?? SECTION_LABEL.get(screen.section)}</li>
                <Crumbs screenId={screen.id} />
              </>
            ) : null}
          </ol>
        </nav>

        <ActivityMoment />

        <EnginePill />
        <CutoffChip />

        <LocationContext />

        <button
          type="button"
          onClick={onOpenPalette}
          className="inline-flex h-[28px] items-center gap-2 rounded-[var(--radius-control)] border border-line-strong bg-surface px-2 text-small text-ink-muted hover:bg-surface-sunken hover:text-ink"
        >
          <Search size={13} aria-hidden />
          <span className="hidden sm:inline">Search</span>
          <kbd className="font-mono text-badge text-ink-faint">⌘K</kbd>
        </button>

        <NotificationBell onClick={() => setNotificationsOpen(true)} />
        <AttentionCount onClick={() => setTrayOpen(true)} />

        <LangToggle />

        <div className="border-l border-line pl-2">
          {/* v7 CORE-1: the look lives in the person's own menu, saved for them on the server. */}
          <UserMenu onAppearance={onToggleTheme} appearanceLabel={theme} />
        </div>
      </header>

      <AttentionTray open={trayOpen} onClose={() => setTrayOpen(false)} />
      <NotificationsPanel open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
    </>
  );
}

function NotificationBell({ onClick }: { onClick: () => void }) {
  const { me } = useSession();
  const unread = useQuery({
    queryKey: ['notifications-unread'],
    enabled: Boolean(me?.authenticated),
    refetchInterval: 120_000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
  });

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={unread.data ? `${unread.data} unread notifications` : 'Notifications'}
      className="relative rounded-[var(--radius-control)] p-1.5 text-ink-muted hover:bg-surface-sunken hover:text-ink"
    >
      <Bell size={15} />
      {/* A dot rather than a number: the count is on the panel, and a badge over a bell is read as "some". */}
      {unread.data ? (
        <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-brand" aria-hidden />
      ) : null}
    </button>
  );
}

/** Shows a figure only when it has one — never a placeholder zero. */
function AttentionCount({ onClick }: { onClick: () => void }) {
  const { can } = useSession();
  const query = useQuery({
    queryKey: ['attention-count'],
    enabled: can('attention.view'),
    refetchInterval: 120_000,   // 30 Oct: every open desk asks this all day; two minutes is plenty for a count
    queryFn: async () => {
      const { count, error } = await supabase
        .from('attention_items')
        .select('id', { count: 'exact', head: true })
        .in('status', ['OPEN', 'ACKNOWLEDGED']);
      if (error) throw error;
      return count ?? 0;
    },
  });

  if (!can('attention.view') || query.isLoading || query.isError || !query.data) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex h-[28px] items-center gap-1.5 rounded-[var(--radius-control)] px-2 text-small',
        'text-queued bg-queued-ground hover:opacity-85',
      )}
      title={`${query.data} item${query.data === 1 ? '' : 's'} need attention`}
    >
      <TriangleAlert size={13} aria-hidden />
      <span className="tabular font-medium">{query.data}</span>
    </button>
  );
}

/** The engine pill (v7 Part D §10.8): how tomorrow stands, in a word and a colour; it opens My day. */
function EnginePill() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ['my-day'], enabled: can('delivery.view'), refetchInterval: 180_000, retry: false,
    queryFn: () => rpc<{ steps: { step: string; state: string; words: string; at: string | null }[] }>('my_day') });
  if (!q.data) return null;
  const prep = q.data.steps.find((x) => x.step === 'Tomorrow prepared');
  const cut = q.data.steps.find((x) => x.step === 'Cut-off and packets');
  const failed = prep?.state === 'FAILED' || cut?.state === 'FAILED';
  const ready = prep?.state === 'OK' && cut?.state === 'OK';
  const words = failed ? 'Tomorrow: needs a look' : ready ? `Tomorrow: ready · ${cut!.words}` : 'Tomorrow: not prepared yet';
  return (
    <Link to="/home" title={failed ? (prep?.state === 'FAILED' ? prep.words : cut?.words) : words}
      className={cx('hidden shrink-0 items-center rounded-full border px-2.5 py-0.5 text-badge font-semibold lg:inline-flex',
        failed ? 'border-refused/40 bg-refused-ground text-refused' : ready ? 'border-accepted/40 bg-accepted-ground text-accepted' : 'border-line bg-surface text-ink-muted')}>
      {words}
    </Link>
  );
}

/** The breadcrumb after the workspace (v7 Part E). */
function Crumbs({ screenId }: { screenId: string }) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const record = useRecordTitle(pathname);
  const screen = screenById(screenId)!;
  const list = RECORD_HOME[screenId] ? screenById(RECORD_HOME[screenId]!) : undefined;
  const tab = HUBS[screenId]?.find((t) => t.screen === params.get('tab')) ?? HUBS[screenId]?.[0];
  const last = list ? record ?? menuLabel(screen) : tab ? tab.label : menuLabel(screen);
  return (
    <>
      {list ? (<><li aria-hidden>/</li><li><Link to={list.path} className="hover:text-ink">{menuLabel(list)}</Link></li></>) : null}
      {tab ? (<><li aria-hidden>/</li><li>{menuLabel(screen)}</li></>) : null}
      <li aria-hidden>/</li>
      <li className="truncate font-medium text-ink">{last}</li>
    </>
  );
}

/**
 * Tomorrow's cut-off, always in view (D19 phase 21; the kit's top bar, pattern 08 "cut-off clock"). Read from
 * api.business_day (v7 CORE-0): tomorrow is the organisation's tomorrow on the server's clock, and the cut-off is the
 * seeded delivery.order_cutoff (every organisation has one since CORE-0, so there is no "not set" state). Before the
 * cut-off it is information; after it, it turns red and says who may still change tomorrow (the CRM manager only).
 * Not shown to a person without delivery.view. Mounting it also anchors lib/businessDay to the server's clock.
 */
function CutoffChip() {
  const { can } = useSession();
  const day = useBusinessDay();
  // v7 CORE-1: only for those who change tomorrow — the CRM side and operations.
  if (!(can('subscription.manage') || can('delivery.plan.manage')) || !day.data) return null;
  const { cutoffTime, tomorrowCutoffAt, tomorrow: kal } = day.data;
  const passed = serverNow() > Date.parse(tomorrowCutoffAt);
  const tomorrow = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${kal}T00:00:00Z`));
  const tone = passed ? 'border-refused/40 bg-refused-ground text-refused' : 'border-info/40 bg-info-ground text-info';
  const words = passed ? `Kal ${tomorrow} · cut-off passed · CRM manager only` : `Changes for tomorrow lock at ${cutoffTime}`;
  return (
    <span title={passed ? 'After the cut-off only the CRM manager can change tomorrow; after packing starts, only skips.' : 'Changes for tomorrow lock at the cut-off.'}
      className={cx('hidden shrink-0 items-center rounded-full border px-2.5 py-0.5 text-badge font-semibold md:inline-flex', tone)}>
      {words}
    </span>
  );
}

/** The हि toggle: the menu's workspace and group names in Hindi (screen names follow when the dictionary is ported). */
function LangToggle() {
  const [lang, setLang] = useNavLang();
  return (
    <button type="button" onClick={() => setLang(lang === 'hi' ? 'en' : 'hi')} aria-pressed={lang === 'hi'}
      aria-label={lang === 'hi' ? 'Menu in English' : 'Menu in Hindi'} title={lang === 'hi' ? 'Menu in English' : 'मेनू हिंदी में'}
      className={cx('rounded-[var(--radius-control)] px-1.5 py-0.5 text-small font-semibold',
        lang === 'hi' ? 'bg-brand text-on-brand' : 'text-ink-muted hover:bg-surface-sunken hover:text-ink')}>
      <span lang="hi">हि</span>
    </button>
  );
}
